import { FORM_ERROR } from 'final-form';
import { capitalize, isEmpty, isPlainObject, pick } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import {
  ALL_OPTIONS,
  buildDuplicatePayload,
  duplicationText,
  isDuplicationTarget,
} from '../../../constants/initiativeDuplication.js';
import { duplicateInitiative } from '../../../modules/initiativeDuplications.js';
import {
  retrieveProject,
  retrieveProjects,
  selectProjects,
} from '../../../modules/projects.js';
import FieldGroup from '../../FieldGroup.jsx';
import { FormError } from '../../FormError.jsx';
import TargetProjectNotice from './TargetProjectNotice.jsx';
import WhatToCopyField from './WhatToCopyField.jsx';
import projectStyles from '../../projects/duplicate/DuplicateProjectModal.module.css';
import styles from './DuplicateInitiativeModal.module.css';

const TITLE_ID = 'duplicateInitiativeModalTitle';

// Server errors are keyed by the field they belong to; the rest is shown under the form.
const FIELDS = ['title', 'projectId'];

// 5xx and network errors come as plain strings without a message.
const mapErrors = (error) => {
  if (!error || typeof error !== 'object' || !error.message) {
    return { [FORM_ERROR]: duplicationText('failed') };
  }

  const fieldErrors = isPlainObject(error.errors)
    ? pick(error.errors, FIELDS)
    : {};

  return isEmpty(fieldErrors) ? { [FORM_ERROR]: error.message } : fieldErrors;
};

const validate = (values) => {
  const errors = {};

  if (!values.title?.trim()) errors.title = duplicationText('titleRequired');
  if (!values.projectId) errors.projectId = duplicationText('projectRequired');

  return errors;
};

const mapStateToProps = (state) => ({
  projects: selectProjects(state),
});

const mapDispatchToProps = {
  duplicateInitiative,
  retrieveProject,
  retrieveProjects,
};

const DuplicateInitiativeModal = ({
  initiative,
  project,
  show,
  onHide,
  projects,
  duplicateInitiative,
  retrieveProject,
  retrieveProjects,
}) => {
  const sourceIsTarget = isDuplicationTarget(project, initiative.type);

  // Fresh list, so projects archived or being duplicated meanwhile are not offered.
  useEffect(() => {
    retrieveProjects().catch(() => {});
  }, []);

  // Only active projects where the user may create this initiative type; archived ones never appear.
  const projectOptions = useMemo(() => {
    const options = projects.filter((item) =>
      isDuplicationTarget(item, initiative.type)
    );

    if (sourceIsTarget && !options.some(({ id }) => id === project.id)) {
      options.unshift(project);
    }

    return options;
  }, [projects, project, initiative.type, sourceIsTarget]);

  // An archived source has no default target: the user has to pick one.
  const initialValues = useMemo(
    () => ({
      title: `${initiative.title} ${duplicationText('titleSuffix')}`,
      projectId: sourceIsTarget ? String(project.id) : '',
      options: ALL_OPTIONS,
    }),
    [initiative.id]
  );

  // A failed copy saves nothing and keeps the modal open with the error.
  // A copy in the same project is added to its list, so the project is loaded again.
  const onSubmit = (values) =>
    duplicateInitiative(initiative, project.id, buildDuplicatePayload(values))
      .then((copy) => {
        if (copy.projectId === project.id) {
          retrieveProject(project.id).catch(() => {});
        }

        onHide();
      })
      .catch(mapErrors);

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName={projectStyles.modal}
      aria-labelledby={TITLE_ID}
    >
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
        render={({ handleSubmit, submitting, submitError, values }) => {
          const targetProject = projectOptions.find(
            ({ id }) => String(id) === values.projectId
          );

          return (
            <form onSubmit={handleSubmit} noValidate>
              <Modal.Header closeButton className={projectStyles.modalHeader}>
                <Modal.Title
                  id={TITLE_ID}
                  className={projectStyles.modalTiitle}
                >
                  {duplicationText('title')}
                </Modal.Title>
                <div className={`flex ${styles.source}`}>
                  <span>
                    <span className={styles.softText}>
                      {duplicationText('source')}{' '}
                    </span>
                    <strong>{initiative.title}</strong>
                  </span>
                  <span className={styles.typeChip}>
                    {capitalize(initiative.type)}
                  </span>
                </div>
              </Modal.Header>

              <Modal.Body>
                <Field
                  id="duplicateInitiativeTitle"
                  name="title"
                  label={duplicationText('titleLabel')}
                  type="text"
                  component={FieldGroup}
                />

                <Field
                  id="duplicateInitiativeProject"
                  name="projectId"
                  label={duplicationText('projectLabel')}
                  componentClass="select"
                  component={FieldGroup}
                >
                  {sourceIsTarget ? null : (
                    <option value="" disabled>
                      {duplicationText('projectPlaceholder')}
                    </option>
                  )}
                  {projectOptions.map(({ id, name }) => (
                    <option key={id} value={String(id)}>
                      {name}
                    </option>
                  ))}
                </Field>

                {targetProject && targetProject.id !== project.id ? (
                  <TargetProjectNotice
                    initiative={initiative}
                    targetProject={targetProject}
                    copyStakeholders={values.options.includes('stakeholders')}
                  />
                ) : null}

                <WhatToCopyField />

                {submitError ? <FormError submitError={submitError} /> : null}
              </Modal.Body>

              <Modal.Footer>
                <Row>
                  <Col sm={6}>
                    <Button block disabled={submitting} onClick={onHide}>
                      Cancel
                    </Button>
                  </Col>
                  <Col sm={6}>
                    <Button
                      block
                      bsStyle="primary"
                      type="submit"
                      disabled={submitting}
                    >
                      {duplicationText(submitting ? 'duplicating' : 'submit')}
                    </Button>
                  </Col>
                </Row>
              </Modal.Footer>
            </form>
          );
        }}
      />
    </Modal>
  );
};

DuplicateInitiativeModal.propTypes = {
  initiative: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    archived: PropTypes.bool,
  }).isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DuplicateInitiativeModal);
